/* eslint-disable no-await-in-loop, no-restricted-syntax */
import * as PDFJS from 'pdfjs-dist/webpack.mjs';
import { MAX_PDF_PAGES } from 'services/imports/limits';
import { pdfjsAssetOptions, type PdfJsAssetOptions } from 'services/pdfjs/assets';

/**
 * The backend parses request bodies with a 5 MB limit and the image travels
 * as base64 (+33% size). The cap is measured on the base64 payload (the part
 * of the data URL after the comma), which is what actually goes on the wire.
 */
export const MAX_OCR_BASE64_CHARS = 4_500_000;

const PDF_SCALE = 3;
const JPEG_QUALITY = 0.8;
const DOWNSCALE_STEP = 0.8;
const MAX_REENCODE_ATTEMPTS = 8;

export type ImportPage = {
  id: string;
  fileName: string;
  pageNumber: number;
  imageDataUrl: string;
  mime: string;
  width: number;
  height: number;
  failed?: string;
};

type PdfViewport = { width: number; height: number };
type PdfPage = {
  getViewport: (params: { scale: number }) => PdfViewport;
  render: (params: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }) => { promise: Promise<unknown> };
  cleanup: () => void;
};
type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};
type PdfDocumentLoadingTask = {
  promise: Promise<PdfDocument>;
  destroy: () => Promise<void>;
};
type PdfJsModule = {
  getDocument: (source: { data: Uint8Array } & PdfJsAssetOptions) => PdfDocumentLoadingTask;
};

type EncodeSource = {
  source: HTMLImageElement | HTMLCanvasElement;
  width: number;
  height: number;
};

type Encoded = {
  imageDataUrl: string;
  mime: string;
  width: number;
  height: number;
};

const pdfjs: PdfJsModule = PDFJS;

let idCounter = 0;

const buildId = (fileName: string, pageNumber: number): string => {
  idCounter += 1;
  return `${fileName}-${pageNumber}-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
};

const toErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const failedPage = (fileName: string, message: string, pageNumber = 1): ImportPage => ({
  id: buildId(fileName, pageNumber),
  fileName,
  pageNumber,
  imageDataUrl: '',
  mime: '',
  width: 0,
  height: 0,
  failed: message
});

const base64Length = (dataUrl: string): number => dataUrl.length - (dataUrl.indexOf(',') + 1);

const readAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(new Error(`Could not read file "${file.name}"`));
  reader.readAsDataURL(file);
});

const loadImage = (dataUrl: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Could not decode image'));
  image.src = dataUrl;
});

const drawToCanvas = (input: EncodeSource, scale: number, opaque: boolean): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(input.width * scale));
  canvas.height = Math.max(1, Math.round(input.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context is not available');
  if (opaque) {
    // JPEG has no alpha channel: avoid transparent areas turning black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.drawImage(input.source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const freeCanvas = (canvas: HTMLCanvasElement): void => {
  // eslint-disable-next-line no-param-reassign
  canvas.width = 0;
  // eslint-disable-next-line no-param-reassign
  canvas.height = 0;
};

const encodeCanvas = (input: EncodeSource, scale: number, mime: 'image/png' | 'image/jpeg'): Encoded => {
  const canvas = drawToCanvas(input, scale, mime === 'image/jpeg');
  const imageDataUrl = mime === 'image/jpeg' ? canvas.toDataURL(mime, JPEG_QUALITY) : canvas.toDataURL(mime);
  const { width, height } = canvas;
  freeCanvas(canvas);
  return { imageDataUrl, mime, width, height };
};

/**
 * PNG at full size first; if it exceeds the cap, JPEG q0.8 re-encoded with
 * repeated 0.8 downscale steps. Returns null when nothing fits.
 */
const encodeWithinCap = (input: EncodeSource): Encoded | null => {
  const png = encodeCanvas(input, 1, 'image/png');
  if (base64Length(png.imageDataUrl) <= MAX_OCR_BASE64_CHARS) return png;

  let scale = 1;
  for (let attempt = 0; attempt < MAX_REENCODE_ATTEMPTS; attempt += 1) {
    const jpeg = encodeCanvas(input, scale, 'image/jpeg');
    if (base64Length(jpeg.imageDataUrl) <= MAX_OCR_BASE64_CHARS) return jpeg;
    scale *= DOWNSCALE_STEP;
  }
  return null;
};

const tooLargeMessage = (fileName: string): string => `"${fileName}" is too large to process even after compression`;

const imageToPage = async (file: File): Promise<ImportPage> => {
  const dataUrl = await readAsDataUrl(file);
  const image = await loadImage(dataUrl);
  const width = image.naturalWidth;
  const height = image.naturalHeight;

  if (file.type === 'image/png' && base64Length(dataUrl) <= MAX_OCR_BASE64_CHARS) {
    return {
      id: buildId(file.name, 1), fileName: file.name, pageNumber: 1, imageDataUrl: dataUrl, mime: 'image/png', width, height
    };
  }

  const encoded = encodeWithinCap({ source: image, width, height });
  if (!encoded) return failedPage(file.name, tooLargeMessage(file.name));
  return {
    id: buildId(file.name, 1), fileName: file.name, pageNumber: 1, ...encoded
  };
};

const renderPdfPage = async (pdf: PdfDocument, fileName: string, pageNumber: number): Promise<ImportPage> => {
  const page = await pdf.getPage(pageNumber);
  const canvas = document.createElement('canvas');
  try {
    const viewport = page.getViewport({ scale: PDF_SCALE });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context is not available');
    await page.render({ canvasContext: context, viewport }).promise;
    const encoded = encodeWithinCap({ source: canvas, width: canvas.width, height: canvas.height });
    if (!encoded) return failedPage(fileName, `Page ${pageNumber} is too large to process even after compression`, pageNumber);
    return {
      id: buildId(fileName, pageNumber), fileName, pageNumber, ...encoded
    };
  } finally {
    page.cleanup();
    freeCanvas(canvas);
  }
};

const yieldToEventLoop = (): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, 0);
});

const pdfToPages = async (file: File): Promise<ImportPage[]> => {
  const data = new Uint8Array(await file.arrayBuffer());
  const loadingTask = pdfjs.getDocument({ data, ...pdfjsAssetOptions });
  const pdf = await loadingTask.promise;
  try {
    if (pdf.numPages > MAX_PDF_PAGES) {
      return [failedPage(file.name, `PDF has more than ${MAX_PDF_PAGES} pages`)];
    }
    const pages: ImportPage[] = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      try {
        pages.push(await renderPdfPage(pdf, file.name, pageNumber));
      } catch (error) {
        pages.push(failedPage(file.name, `Page ${pageNumber} failed: ${toErrorMessage(error)}`, pageNumber));
      }
      await yieldToEventLoop();
    }
    return pages;
  } finally {
    await loadingTask.destroy();
  }
};

const isPdf = (file: File): boolean => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
const isImage = (file: File): boolean => file.type.startsWith('image/');

const fileToPages = async (file: File): Promise<ImportPage[]> => {
  try {
    if (isPdf(file)) return await pdfToPages(file);
    if (isImage(file)) return [await imageToPage(file)];
    return [failedPage(file.name, 'Unsupported file type')];
  } catch (error) {
    return [failedPage(file.name, `Could not process "${file.name}": ${toErrorMessage(error)}`)];
  }
};

/** Converts files into OCR-ready page images. Per-file errors become `failed` entries. */
export const filesToPages = async (files: File[]): Promise<ImportPage[]> => {
  const pages: ImportPage[] = [];
  for (const file of files) {
    pages.push(...(await fileToPages(file)));
  }
  return pages;
};
